import { formatDateScalarString } from '@/common/dates';
import { StandardButton } from '@/components/buttons/standard';
import { useCurrentUser } from '@/hooks';
import { PrimaryLayout } from '@/layouts/primary';
import { Flex } from '@mantine/core';
import { useNavigate } from '@tanstack/react-router';
import { FormikProps } from 'formik';
import { useCallback, useMemo, useRef } from 'react';
import { AccountDetailsForm, SignupFormValues } from './form';
import { useUpdateUserDetailsMutation } from './graphql.generated';

export const SignupDetailsPage = () => {
	const navigate = useNavigate();
	const { currentUser } = useCurrentUser({ forceRefetch: true });
	const [updateUserDetails, { loading: updatingUser }] = useUpdateUserDetailsMutation();
	const ref = useRef<FormikProps<SignupFormValues>>(null);
	const saveForm = useCallback(() => {
		ref.current?.submitForm();
	}, []);
	const saveAccountButton = useMemo(
		() => (
			<StandardButton loading={updatingUser} onClick={saveForm}>
				Save Account
			</StandardButton>
		),
		[saveForm, updatingUser]
	);

	const handleSubmit = useCallback(
		async (values: SignupFormValues) => {
			const userId = currentUser?.id;

			if (!userId) {
				return;
			}

			try {
				await updateUserDetails({
					variables: {
						input: {
							id: userId,
							formattedAddress: values.formattedAddress,
							addressLine1: values.addressLine1,
							addressLine2: values.addressLine2,
							city: values.city,
							dateOfBirth: formatDateScalarString(values.dateOfBirth),
							council: values.council,
							dwellingType: values.dwellingType,
							gender: values.gender,
							householdSize: values.householdSize,
							phoneNumber: values.phoneNumber,
							postcode: values.postcode,
							state: values.state,
							lat: values.lat,
							lng: values.lng,
							isManualAddress: values.isManualAddress,
						},
					},
				});

				navigate({ to: '/about-subscriptions' });
			} catch (error) {
				// TODO: need some error handling to give the user feedback
				console.error(error);
			}
		},
		[updateUserDetails, currentUser?.id, navigate]
	);

	return (
		<PrimaryLayout header={{ title: 'Account Details' }} footer={{ children: [saveAccountButton] }}>
			<Flex direction="column" justify="space-between" h="100%" w="100%">
				<Flex direction="column" align="center" justify="flex-start" w="100%">
					<AccountDetailsForm onSubmit={handleSubmit} formRef={ref} />
				</Flex>
			</Flex>
		</PrimaryLayout>
	);
};
